import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";

import { api } from "@/api/client";
import type { Device } from "@/api/types";
import { Modal } from "@/components/Modal";
import { Spinner } from "@/components/ui";
import { useUi } from "@/lib/store";
import { deviceName } from "@/lib/dashboard/params";

/**
 * Which of a project's devices the dashboards show.
 *
 * With 120 nodes on one structure, every chart holding every node is the
 * wrong default for anyone looking at one span. The selection is stored per
 * project in this browser, and choosing every device is the same as having no
 * filter - so a device added later is not silently left out.
 */
export function DeviceFilterDialog({
  open,
  onClose,
  projectCode,
}: {
  open: boolean;
  onClose: () => void;
  projectCode: string;
}) {
  const { deviceFilters, setDeviceFilter } = useUi();
  const devices = useQuery({
    queryKey: ["devices", projectCode],
    queryFn: () => api.devices(projectCode),
    enabled: open,
  });
  const types = useQuery({ queryKey: ["device-types"], queryFn: api.deviceTypes, enabled: open });

  const [draft, setDraft] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!open || !devices.data) return;
    // A stored filter can name devices that have since been deleted; keep only
    // the ones that still exist, and start from "all" if none do.
    const existing = new Set(devices.data.map((d) => d.code));
    const kept = (deviceFilters[projectCode] ?? []).filter((code) => existing.has(code));
    setDraft(new Set(kept.length ? kept : devices.data.map((d) => d.code)));
    setQuery("");
  }, [open, devices.data, deviceFilters, projectCode]);

  const groups = useMemo(() => {
    const typeName = new Map((types.data ?? []).map((t) => [t.code, t.label]));
    const q = query.trim().toLowerCase();
    const byType = new Map<string, Device[]>();
    for (const d of devices.data ?? []) {
      if (q && !`${d.label} ${d.display_name ?? ""}`.toLowerCase().includes(q)) continue;
      const name = typeName.get(d.device_type) ?? "Other";
      byType.set(name, [...(byType.get(name) ?? []), d]);
    }
    return [...byType.entries()]
      .map(([name, list]) => ({
        name,
        devices: list.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true })),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [devices.data, types.data, query]);

  const total = devices.data?.length ?? 0;

  function setMany(codes: string[], selected: boolean) {
    const next = new Set(draft);
    for (const code of codes) {
      if (selected) next.add(code);
      else next.delete(code);
    }
    setDraft(next);
  }

  function apply() {
    if (!draft.size) return;
    setDeviceFilter(projectCode, draft.size === total ? null : [...draft]);
    onClose();
  }

  const visibleCodes = groups.flatMap((g) => g.devices.map((d) => d.code));

  return (
    <Modal
      title="Filter devices"
      subtitle="Charts on every dashboard show only the selected devices."
      open={open}
      onClose={onClose}
      width="max-w-lg"
      footer={
        <div className="flex w-full items-center justify-between">
          <button
            type="button"
            className="btn-ghost"
            onClick={() => {
              setDeviceFilter(projectCode, null);
              onClose();
            }}
          >
            Show all devices
          </button>
          <div className="flex gap-2">
            <button type="button" className="btn-ghost" onClick={onClose}>
              Cancel
            </button>
            <button type="button" className="btn-primary" onClick={apply} disabled={!draft.size}>
              Apply
            </button>
          </div>
        </div>
      }
    >
      {devices.isLoading ? (
        <Spinner />
      ) : !total ? (
        <p className="py-6 text-center text-sm text-ink-400">This project has no devices yet.</p>
      ) : (
        <div className="space-y-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-500" />
            <input
              autoFocus
              className="input pl-9"
              placeholder="Search devices, e.g. LDP001"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="flex items-center justify-between text-xs text-ink-400">
            <span>
              {draft.size} of {total} selected
            </span>
            <span className="flex gap-3">
              <button type="button" className="text-brand-300 hover:text-brand-200" onClick={() => setMany(visibleCodes, true)}>
                Select {query ? "matches" : "all"}
              </button>
              <button type="button" className="text-brand-300 hover:text-brand-200" onClick={() => setMany(visibleCodes, false)}>
                Clear {query ? "matches" : "all"}
              </button>
            </span>
          </div>
          {!draft.size && (
            <p className="text-[11px] text-sev-1">
              Select at least one device, or use Show all devices.
            </p>
          )}
          <div className="max-h-96 space-y-3 overflow-y-auto pr-1">
            {groups.map((g) => {
              const codes = g.devices.map((d) => d.code);
              const allOn = codes.every((c) => draft.has(c));
              return (
                <div key={g.name}>
                  <div className="mb-1 flex items-center justify-between">
                    <span className="label">
                      {g.name} · {g.devices.length}
                    </span>
                    <button
                      type="button"
                      className="text-[11px] text-brand-300 hover:text-brand-200"
                      onClick={() => setMany(codes, !allOn)}
                    >
                      {allOn ? "Clear" : "Select all"}
                    </button>
                  </div>
                  <ul className="grid gap-0.5 sm:grid-cols-2">
                    {g.devices.map((d) => (
                      <li key={d.code}>
                        <label className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm text-ink-200 hover:bg-ink-800">
                          <input
                            type="checkbox"
                            className="h-3.5 w-3.5 accent-brand-500"
                            checked={draft.has(d.code)}
                            onChange={() => setMany([d.code], !draft.has(d.code))}
                          />
                          <span className="truncate" title={d.label}>{deviceName(d)}</span>
                          <span
                            className="ml-auto h-1.5 w-1.5 shrink-0 rounded-full"
                            style={{ backgroundColor: d.is_online ? "#3fb27f" : "#5a5a70" }}
                            title={d.is_online ? "Online" : "Offline"}
                          />
                        </label>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
            {!groups.length && <p className="text-xs text-ink-500">No device matches.</p>}
          </div>
        </div>
      )}
    </Modal>
  );
}
